import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useProducts } from "../../../hooks/useProducts";
import { supabase } from "../../../lib/supabase";

export { ProductCard } from "../../../components/base/ProductCard";

const WA_NUMBER = "5511923169450";

// 🔒 PADRÃO GLOBAL (MESMO DO useProducts)
const isVisible = (v: any): boolean =>
  v === true || v === 1 || v === "1" || v === "true";

const getMainImage = (product: any): string => {
  if (typeof product?.image_url === "string" && product.image_url.trim()) return product.image_url;
  if (typeof product?.image === "string" && product.image.trim()) return product.image;

  if (Array.isArray(product?.images)) {
    const first = product.images.find((img: any) => typeof img === "string" && img.trim());
    if (first) return first;
  }

  if (Array.isArray(product?.colors)) {
    for (const color of product.colors) {
      if (Array.isArray(color?.images)) {
        const first = color.images.find((img: any) => typeof img === "string" && img.trim());
        if (first) return first;
      }
      if (typeof color?.image === "string" && color.image.trim()) return color.image;
    }
  }

  return "";
};

const getProductSlug = (product: any): string => {
  if (typeof product?.slug === "string" && product.slug.trim()) return product.slug;
  if (typeof product?.id === "string" && product.id.trim()) return product.id;
  if (typeof product?.id === "number") return String(product.id);
  return "";
};

function NewCollectionCard({ product }: { product: any }) {
  const image = getMainImage(product);
  const slug = getProductSlug(product);

  const waMessage = encodeURIComponent(
    `Olá! Tenho interesse na peça: ${product?.name || "Produto"}`
  );

  const waHref = `https://wa.me/${WA_NUMBER}?text=${waMessage}`;

  return (
    <div className="group flex flex-col snap-start">
      <Link to={`/produto/${slug}`} className="block relative overflow-hidden bg-[#F5F0E8] mb-3">
        {image ? (
          <img
            src={image}
            alt={product?.name || "Produto"}
            className="w-full h-[320px] md:h-[400px] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="w-full h-[320px] md:h-[400px] bg-[#F5F0E8]" />
        )}
      </Link>

      <div className="flex flex-col items-center text-center gap-2 px-1">
        <Link
          to={`/produto/${slug}`}
          className="text-[12px] font-semibold text-black uppercase tracking-[0.06em] leading-snug line-clamp-2 hover:opacity-70 transition-opacity"
        >
          {product?.name || "Produto"}
        </Link>

        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 w-full border border-black/30 py-2.5 text-[11px] tracking-[0.18em] uppercase text-black hover:bg-black hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
        >
          Comprar
        </a>
      </div>
    </div>
  );
}

export default function BestSellers() {
  const { products } = useProducts();
  const [exclusiveProducts, setExclusiveProducts] = useState<any[]>([]);

  useEffect(() => {
    supabase
      .from("ray_melo_products")
      .select("*")
      .then(({ data }) => {
        const visible = (data || []).filter((p: any) => isVisible(p?.visible));
        setExclusiveProducts(visible);
      })
      .catch(() => setExclusiveProducts([]));
  }, []);

  const featured = useMemo(() => {
    // 🔒 GARANTE VISÍVEL + FEATURED
    const featuredShared = (products || [])
      .filter((p: any) => isVisible(p?.visible) && isVisible(p?.featured))
      .map((p: any) => ({ ...p, source: "shared" }));

    const exclusiveMapped = (exclusiveProducts || [])
      .filter((p: any) => isVisible(p?.visible))
      .map((p: any) => ({ ...p, source: "exclusive" }));

    const combined = [...exclusiveMapped, ...featuredShared];

    const seen = new Set<string>();

    return combined
      .filter((p: any) => {
        const key = String(p.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 12);
  }, [products, exclusiveProducts]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;

    const el = scrollRef.current;
    const amount = el.clientWidth / 4;

    if (dir === "right" && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else if (dir === "left" && el.scrollLeft <= 4) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else {
      el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    autoRef.current = setInterval(() => scroll("right"), 3000);
    return () => {
      if (autoRef.current) clearInterval(autoRef.current);
    };
  }, [featured]);

  const pauseAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
  };

  const resumeAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => scroll("right"), 3000);
  };

  if (featured.length === 0) return null;

  return (
    <section className="py-14 px-4 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">

        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-black/15" />
          <h2 className="text-[14px] tracking-[0.3em] font-semibold text-black uppercase px-2">
            Nova Coleção
          </h2>
          <div className="flex-1 h-px bg-black/15" />
        </div>

        <div className="relative">

          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center border border-black/20 bg-white hover:bg-black hover:text-white transition-all ${
              canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            ‹
          </button>

          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center border border-black/20 bg-white hover:bg-black hover:text-white transition-all ${
              canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            ›
          </button>

          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            onMouseEnter={pauseAuto}
            onMouseLeave={resumeAuto}
            className="grid grid-flow-col auto-cols-[calc(50%-8px)] md:auto-cols-[calc(33.333%-11px)] lg:auto-cols-[calc(25%-12px)] gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-1"
          >
            {featured.map((product: any) => (
              <NewCollectionCard key={product.id} product={product} />
            ))}
          </div>

        </div>

        <div className="text-center mt-10">
          <Link
            to="/catalogo"
            className="inline-block border border-black/25 px-10 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-black hover:text-white transition-all"
          >
            Ver Todos os Produtos
          </Link>
        </div>

      </div>
    </section>
  );
}